/**
 * Położenie falownika w scenie (krok „Falownik”, prośba właściciela 2026-10-11).
 *
 * Na dachu: falownik na zewnątrz wisi na ścianie budynku. Domyślnie na ścianie skrzydła głównego
 * zwróconej najbardziej na północ (w cieniu, falownik nie lubi słońca), w połowie długości, środek
 * na wysokości 1,45 m. Użytkownik wybiera inną ścianę i położenie wzdłuż niej (0..1).
 * Na gruncie: pod modułami, na nogach konstrukcji od tylnej (wyższej) strony, środek na 0,6 m.
 * W środku budynku: brak modelu w scenie.
 */

const st = (r) => (r * 180) / Math.PI

/** Ściany z dolną krawędzią (do wyboru), z azymutem normalnej. */
export const scianyDoFalownika = (bryla) => (bryla.sciany || []).map((s, i) => {
  const ymin = Math.min(...s.wierzcholki.map((p) => p[1]))
  const dol = s.wierzcholki.filter((p) => Math.abs(p[1] - ymin) < 1e-3)
  if (dol.length < 2) return null
  let az = st(Math.atan2(s.normalna[0], -s.normalna[2]))
  if (az < 0) az += 360
  return { indeks: i, skrzydloId: s.skrzydloId, strona: s.strona, a: dol[0], b: dol[dol.length - 1], normalna: s.normalna, azymut: az, dlugosc: Math.hypot(dol[dol.length - 1][0] - dol[0][0], dol[dol.length - 1][2] - dol[0][2]) }
}).filter((s) => s && s.dlugosc > 1.2)

/** Domyślna ściana: skrzydła głównego (pierwszego), zwrócona najbardziej na północ. */
export const domyslnaSciana = (sciany, glowneSkrzydloId) => {
  const kandydaci = sciany.filter((s) => !glowneSkrzydloId || s.skrzydloId === glowneSkrzydloId)
  const lista = kandydaci.length ? kandydaci : sciany
  return lista.reduce((best, s) => {
    const odPolnocy = Math.min(s.azymut, 360 - s.azymut)
    return !best || odPolnocy < best.odPolnocy ? { ...s, odPolnocy } : best
  }, null)
}

/**
 * @returns { pozycja: [x,y,z], obrot (stopnie, jak rotation.y), sciana?: indeks } albo null (w środku, brak miejsca)
 */
export const polozenieFalownika = ({ projekt, bryla, uklad }) => {
  const f = projekt.falownik || {}
  if (f.miejsce === 'wewnatrz') return null
  if (projekt.rodzaj === 'grunt') {
    const nogi = uklad?.nogi || []
    if (!nogi.length) return null
    // Tylna linia nóg: najwyższe nogi; z nich najbliższa środka konstrukcji.
    const maksH = Math.max(...nogi.map((n) => n.wysokosc))
    const tylne = nogi.filter((n) => n.wysokosc > maksH - 0.05)
    const sx = nogi.reduce((s, n) => s + n.podstawa[0], 0) / nogi.length
    const sz = nogi.reduce((s, n) => s + n.podstawa[2], 0) / nogi.length
    const noga = tylne.reduce((b, n) => (!b || Math.hypot(n.podstawa[0] - sx, n.podstawa[2] - sz) < Math.hypot(b.podstawa[0] - sx, b.podstawa[2] - sz) ? n : b), null)
    // Front falownika na zewnątrz konstrukcji (od środka ku tylnej nodze).
    const dx = noga.podstawa[0] - sx
    const dz = noga.podstawa[2] - sz
    const l = Math.hypot(dx, dz) || 1
    const obrot = st(Math.atan2(dx / l, dz / l))
    return { pozycja: [noga.podstawa[0] + (dx / l) * 0.16, noga.podstawa[1] + 0.6, noga.podstawa[2] + (dz / l) * 0.16], obrot }
  }
  const sciany = scianyDoFalownika(bryla)
  if (!sciany.length) return null
  const glowne = projekt.budynek?.skrzydla?.[0]?.id
  const sciana = (f.sciana != null && sciany.find((s) => s.indeks === f.sciana)) || domyslnaSciana(sciany, glowne)
  const t = Math.min(0.9, Math.max(0.1, f.polozenie ?? 0.5))
  const [nx, , nz] = sciana.normalna
  const x = sciana.a[0] + (sciana.b[0] - sciana.a[0]) * t + nx * 0.13
  const z = sciana.a[2] + (sciana.b[2] - sciana.a[2]) * t + nz * 0.13
  return { pozycja: [x, sciana.a[1] + 1.45, z], obrot: st(Math.atan2(nx, nz)), sciana: sciana.indeks }
}
